import { pokerGameAPI, cricketGameAPI, pingpongGameAPI, eventsAPI } from '../../services/api';
import { pickleballGameAPI } from '../../scoring/pickleball/services/pickleballGameApi';
import { apiCall } from '../../services/apiUtil';
import { formatEventDate, formatEventTime } from '../../utils/dateTimeUtils';

/**
 * SquadDetailsProvider - Centralized service class to manage GroupDetailVO
 *
 * All page initialization and data fetching logic should go through this provider.
 * Components should request data from this provider rather than fetching directly.
 *
 * Usage:
 *   const provider = new SquadDetailsProvider(groupId);
 *   await provider.initialize(urlParams, { getGroup, getGroupMembers });
 *   const members = provider.getParticipatingMembers();
 */
class SquadDetailsProvider {
  constructor(groupId) {
    this.vo = {
      groupId: groupId,
      group: null,
      members: [],
      events: [],
      currentEvent: {
        eventId: null,
        eventData: null,
        games: {
          poker: null,
          cricket: null,
          pingpong: null,
          pickleball: null
        }
      },
      existingGames: {
        poker: {},
        cricket: {},
        pingpong: {},
        pickleball: {}
      },
      // URL parameters tracking
      urlParams: {
        eventId: null,
        gameId: null,
        openGame: false,
        tab: null
      }
    };

    // Initialization state
    this._initialized = false;
    this._initializing = false;
    this._initPromise = null;

    // Callbacks for state updates (to sync with React state)
    this._stateChangeListeners = [];
  }

  /**
   * Add callback for state changes (to sync with React state)
   * Returns a function to remove the listener
   */
  addStateChangeListener(callback) {
    this._stateChangeListeners.push(callback);
    return () => {
      this._stateChangeListeners = this._stateChangeListeners.filter(cb => cb !== callback);
    };
  }

  /**
   * Set callback for state changes (backward compatibility)
   * @deprecated Use addStateChangeListener instead
   */
  setOnStateChange(callback) {
    // Clear existing listeners and add new one
    this._stateChangeListeners = callback ? [callback] : [];
  }

  /**
   * Notify state change to all listeners
   */
  _notifyStateChange(changeType, data) {
    this._stateChangeListeners.forEach(callback => {
      try {
        callback(changeType, data);
      } catch (error) {
        console.error('[SquadDetailsProvider] Error in state change listener:', error);
      }
    });
  }

  /**
   * Check if provider is initialized
   */
  isInitialized() {
    return this._initialized;
  }

  /**
   * Wait for initialization to complete
   */
  async waitForInitialization() {
    if (this._initialized) return this.vo;
    if (this._initPromise) return this._initPromise;
    throw new Error('Provider not initialized. Call initialize() first.');
  }

  /**
   * Parse URL parameters
   */
  parseUrlParams(searchParams) {
    const eventId = searchParams.get('eventId');
    const gameId = searchParams.get('gameId');
    const openGame = searchParams.get('openGame') === 'true';
    const tab = searchParams.get('tab');

    return {
      eventId: eventId ? parseInt(eventId) : null,
      gameId: gameId ? parseInt(gameId) : null,
      openGame,
      tab
    };
  }

  /**
   * Check if URL parameters have changed
   */
  hasUrlParamsChanged(newParams) {
    const current = this.vo.urlParams;
    return (
      current.eventId !== newParams.eventId ||
      current.gameId !== newParams.gameId ||
      current.openGame !== newParams.openGame ||
      current.tab !== newParams.tab
    );
  }

  /**
   * Main initialization method - must complete before any other operations
   *
   * @param {URLSearchParams} searchParams - URL search parameters
   * @param {Object} hooks - Object containing hook functions { getGroup, getGroupMembers }
   * @param {Object} options - Additional options { forceRefresh: boolean }
   * @returns {Promise<Object>} The initialized VO
   */
  async initialize(searchParams, hooks, options = {}) {
    const { getGroup, getGroupMembers } = hooks;
    this._hooks = hooks;
    const { forceRefresh = false } = options;

    // Parse URL parameters
    const newUrlParams = this.parseUrlParams(searchParams);
    const urlParamsChanged = this.hasUrlParamsChanged(newUrlParams);

    // If already initialized and nothing changed, return existing VO
    if (this._initialized && !forceRefresh && !urlParamsChanged) {
      return this.vo;
    }

    // If initialization is in progress, wait for it
    if (this._initializing && this._initPromise) {
      return this._initPromise;
    }

    // Start initialization
    this._initializing = true;

    this._initPromise = this._doInitialize(newUrlParams, getGroup, getGroupMembers, forceRefresh);

    try {
      const result = await this._initPromise;
      this._initialized = true;
      this._initializing = false;
      return result;
    } catch (error) {
      this._initializing = false;
      this._initPromise = null;
      console.error('[SquadDetailsProvider] Initialization failed:', error);
      throw error;
    }
  }

  /**
   * Internal initialization logic
   */
  async _doInitialize(newUrlParams, getGroup, getGroupMembers, forceRefresh) {
    const groupId = this.vo.groupId;

    try {
      // Step 1: Fetch group data (if not already loaded or force refresh)
      if (!this.vo.group || forceRefresh) {
        const groupResult = await getGroup(groupId);
        if (groupResult.success && groupResult.data) {
          this.vo.group = groupResult.data;
          this._notifyStateChange('group', groupResult.data);
        } else {
          throw new Error(groupResult.error || 'Failed to fetch group');
        }
      }

      // Step 2: Fetch members (if not already loaded or force refresh)
      if (this.vo.members.length === 0 || forceRefresh) {
        const membersResult = await getGroupMembers(groupId, 1, 50);
        if (membersResult.success && membersResult.data) {
          this.vo.members = membersResult.data;
          this._notifyStateChange('members', membersResult.data);
        }
      }

      // Step 3: Fetch events
      const events = await this._fetchEvents(newUrlParams.eventId);
      this.vo.events = events;
      this._notifyStateChange('events', events);

      // Step 4: Process URL parameters - load specific event and game if specified
      await this._processUrlParams(newUrlParams);

      // Update stored URL params
      this.vo.urlParams = newUrlParams;

      return this.vo;
    } catch (error) {
      console.error('[SquadDetailsProvider] Error in initialization:', error);
      throw error;
    }
  }

  /**
   * Fetch events from API
   */
  async _fetchEvents(targetEventId) {
    const groupId = this.vo.groupId;

    const formatDate = (d) => {
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    // If we have a target eventId (from URL), expand date range to include past events
    const past = new Date();
    past.setDate(past.getDate() - (targetEventId ? 30 : 7));
    const future = new Date();
    future.setDate(future.getDate() + 30);

    try {
      const params = new URLSearchParams({
        ownerType: 'group',
        ownerId: groupId,
        startDate: formatDate(past),
        endDate: formatDate(future),
        // When navigating to a specific event (e.g. from My Games), include past
        // events so completed games are found even if end_time < now
        ...(targetEventId && { includePast: 'true' })
      });


      const eventsList = await apiCall(`/events?${params.toString()}`);

      // Transform events — use same schema as eventsApi/useEvents so EventInfo can
      // consume provider events (start_time/end_time) and display properties work.
      return eventsList.map(event => {
        const base = {
          id: event.id,
          ownerType: event.ownerType,
          ownerId: event.ownerId,
          subject: event.subject,
          venue: event.venue || null,
          start_time: event.start_time,  // ISO timestamp (UTC)
          end_time: event.end_time,      // ISO timestamp (UTC)
          status: event.status || 'open',
          createdAt: event.createdAt,
          createdBy: event.createdBy,
          // 'invitees' when the event is for the people invited to it; null for
          // an older event, which is for the whole squad.
          audience: event.audience ?? null,
          responses: (event.responses || []).map(r => ({
            userId: r.userId,
            user: r.user,
            username: r.user?.first_name && r.user?.last_name
              ? `${r.user.first_name} ${r.user.last_name}`
              : `User ${r.userId}`,
            status: r.status
          }))
        };
        // Enrich with pre-formatted display properties (matching useEvents format)
        return {
          ...base,
          start_date_display: formatEventDate(base.start_time),
          start_time_display: formatEventTime(base.start_time),
          end_date_display: formatEventDate(base.end_time),
          end_time_display: formatEventTime(base.end_time),
          start_datetime_display: `${formatEventDate(base.start_time)} at ${formatEventTime(base.start_time)}`,
          end_datetime_display: `${formatEventDate(base.end_time)} at ${formatEventTime(base.end_time)}`,
        };
      });
    } catch (error) {
      console.error('[SquadDetailsProvider] Error fetching events:', error);
      return [];
    }
  }

  /**
   * Get current user ID from auth service
   */
  _getCurrentUserId() {
    try {
      const userStr = localStorage.getItem('currentUser');
      if (userStr) {
        const user = JSON.parse(userStr);
        return user?.id?.toString() || '1';
      }
    } catch (e) {
      console.error('Error getting current user:', e);
    }
    return '1';
  }

  /**
   * Process URL parameters to load specific event and game
   */
  async _processUrlParams(urlParams) {
    const { eventId, gameId, openGame, tab } = urlParams;

    if (!eventId) {
      // No specific event requested, get next upcoming event
      const upcomingEvent = this.getNextUpcomingEvent();
      if (upcomingEvent) {
        this.setCurrentEvent(upcomingEvent);
        // Load game for upcoming event if sport type supports it
        await this._loadGameForCurrentEvent();
      }
      return;
    }

    // Find the requested event
    const targetEvent = this.vo.events.find(e => e.id === eventId);
    if (!targetEvent) {
      console.warn('[SquadDetailsProvider] Event not found:', eventId);
      // Fall back to next upcoming event
      const upcomingEvent = this.getNextUpcomingEvent();
      if (upcomingEvent) {
        this.setCurrentEvent(upcomingEvent);
        await this._loadGameForCurrentEvent();
      }
      return;
    }

    // Set the target event as current
    this.setCurrentEvent(targetEvent);
    this._notifyStateChange('currentEvent', targetEvent);

    // Load the game for this event
    await this._loadGameForCurrentEvent(gameId);
  }

  /**
   * Load game data for the current event
   */
  async _loadGameForCurrentEvent(targetGameId = null) {
    const event = this.vo.currentEvent.eventData;
    if (!event) return null;

    const sportName = this.vo.group?.sportType?.sportName?.toLowerCase();
    if (!sportName) return null;

    const eventId = event.id;
    let gameData = null;

    try {
      if (sportName === 'poker') {
        // Check cache first
        gameData = this.vo.existingGames.poker[eventId];
        if (!gameData) {
          const games = await pokerGameAPI.getByEventId(eventId);
          if (Array.isArray(games) && games.length > 0) {
            // If specific gameId requested, find it; otherwise use first
            gameData = targetGameId
              ? games.find(g => g.id === targetGameId) || games[0]
              : games[0];
            this.cacheGame('poker', eventId, gameData);
          }
        }
        if (gameData) {
          this.setCurrentGame('poker', gameData);
          this._notifyStateChange('currentPokerGame', gameData);
        }
      } else if (sportName === 'cricket') {
        gameData = this.vo.existingGames.cricket[eventId];
        if (!gameData) {
          const games = await cricketGameAPI.getByEventId(eventId);
          if (Array.isArray(games) && games.length > 0) {
            gameData = targetGameId
              ? games.find(g => g.id === targetGameId) || games[0]
              : games[0];
            this.cacheGame('cricket', eventId, gameData);
          }
        }
        if (gameData) {
          this.setCurrentGame('cricket', gameData);
          this._notifyStateChange('currentCricketGame', gameData);
        }
      } else if (sportName === 'ping pong' || sportName === 'table tennis') {
        gameData = this.vo.existingGames.pingpong[eventId];
        if (!gameData) {
          const games = await pingpongGameAPI.getByEventId(eventId);
          if (Array.isArray(games) && games.length > 0) {
            gameData = targetGameId
              ? games.find(g => g.id === targetGameId) || games[0]
              : games[0];
            this.cacheGame('pingpong', eventId, gameData);
          }
        }
        if (gameData) {
          this.setCurrentGame('pingpong', gameData);
          this._notifyStateChange('currentPingpongGame', gameData);
        }
      } else if (sportName === 'pickleball') {
        gameData = this.vo.existingGames.pickleball[eventId];
        if (!gameData) {
          const games = await pickleballGameAPI.getByEventId(eventId);
          if (Array.isArray(games) && games.length > 0) {
            gameData = targetGameId
              ? games.find(g => g.id === targetGameId) || games[0]
              : games[0];
            this.cacheGame('pickleball', eventId, gameData);
          }
        }
        if (gameData) {
          this.setCurrentGame('pickleball', gameData);
          this._notifyStateChange('currentPickleballGame', gameData);
        }
      }

      return gameData;
    } catch (error) {
      console.error('[SquadDetailsProvider] Error loading game:', error);
      return null;
    }
  }

  /**
   * Handle URL parameter changes (called when URL changes without full page reload)
   */
  async handleUrlChange(searchParams) {
    const newUrlParams = this.parseUrlParams(searchParams);

    if (!this.hasUrlParamsChanged(newUrlParams)) {
      return this.vo;
    }


    // Update urlParams BEFORE processing so that when _notifyStateChange('currentEvent')
    // fires inside _processUrlParams, listeners can already read the new urlParams.
    this.vo.urlParams = newUrlParams;

    // If navigating to a specific event, re-fetch the events list so that past/completed
    // events (e.g. games clicked from My Games) are included. The initial load only
    // fetches upcoming events, so a completed game won't be in this.vo.events yet.
    if (newUrlParams.eventId) {
      const events = await this._fetchEvents(newUrlParams.eventId);
      this.vo.events = events;
      this._notifyStateChange('events', events);
    }

    await this._processUrlParams(newUrlParams);

    return this.vo;
  }

  /**
   * Reset provider for a new groupId
   */
  reset(newGroupId) {

    this.vo = {
      groupId: newGroupId,
      group: null,
      members: [],
      events: [],
      currentEvent: {
        eventId: null,
        eventData: null,
        games: {
          poker: null,
          cricket: null,
          pingpong: null,
          pickleball: null
        }
      },
      existingGames: {
        poker: {},
        cricket: {},
        pingpong: {},
        pickleball: {}
      },
      urlParams: {
        eventId: null,
        gameId: null,
        openGame: false,
        tab: null
      }
    };

    this._initialized = false;
    this._initializing = false;
    this._initPromise = null;
  }

  // ==================== Getters ====================

  /**
   * Get the current VO
   */
  getVO() {
    return this.vo;
  }

  /**
   * Get group data
   */
  getGroup() {
    return this.vo.group;
  }

  /**
   * Get members list
   */
  getMembers() {
    return this.vo.members;
  }

  /**
   * Get events list
   */
  getEvents() {
    return this.vo.events;
  }

  /**
   * Get current event data
   */
  getCurrentEvent() {
    return this.vo.currentEvent.eventData;
  }

  /**
   * Get current game for a specific sport type
   */
  getCurrentGame(gameType) {
    return this.vo.currentEvent.games[gameType] || null;
  }

  /**
   * Get sport name from group
   */
  getSportName() {
    return this.vo.group?.sportType?.sportName?.toLowerCase() || null;
  }

  /**
   * Get current user ID (public wrapper for _getCurrentUserId)
   */
  getCurrentUserId() {
    return this._getCurrentUserId();
  }

  /**
   * Create a new event for the group
   */
  async createEvent(eventData) {
    try {
      const payload = {
        ownerType: 'group',
        ownerId: this.vo.groupId,
        subject: eventData.subject,
        address: eventData.address || '',
        date: eventData.date,
        startTime: eventData.startTime,
        endTime: eventData.endTime
      };

      const result = await apiCall('/events', {
        method: 'POST',
        body: JSON.stringify(payload)
      });

      // Refresh events list
      await this.refreshEvents();

      return { success: true, data: result };
    } catch (error) {
      console.error('Error creating event:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Refresh events list from API
   */
  async refreshEvents() {
    try {
      const events = await this._fetchEvents(null);
      this.vo.events = events;
      this._notifyStateChange('events', events);
      return events;
    } catch (error) {
      console.error('[SquadDetailsProvider] Error refreshing events:', error);
      throw error;
    }
  }

  /**
   * Refresh group data and members from API
   */
  async refreshGroup() {
    try {
      const { getGroup, getGroupMembers } = this._hooks || {};
      if (!getGroup) throw new Error('getGroup hook not available');
      const groupId = this.vo.groupId;
      const groupResult = await getGroup(groupId);
      if (groupResult.success) {
        this.vo.group = groupResult.data;
        this._notifyStateChange('group', groupResult.data);
      }
      if (getGroupMembers) {
        const membersResult = await getGroupMembers(groupId, 1, 50);
        if (membersResult.success && membersResult.data) {
          this.vo.members = membersResult.data;
          this._notifyStateChange('members', membersResult.data);
        }
      }
      return this.vo;
    } catch (error) {
      console.error('[SquadDetailsProvider] Error refreshing group:', error);
      throw error;
    }
  }

  /**
   * Get participating members for an event (those who responded available or join-late)
   * This is the centralized method that should be used by all components
   */
  getParticipatingMembers(event = null) {
    const eventToUse = event || this.vo.currentEvent.eventData;

    if (!eventToUse) {
      return [];
    }

    if (!eventToUse.responses || eventToUse.responses.length === 0) {
      return [];
    }

    // Get responses with available or join-late status
    const participatingResponses = eventToUse.responses.filter(r =>
      r.status === 'available' || r.status === 'join-late'
    );

    if (participatingResponses.length === 0) {
      return [];
    }

    // Enrich responses with member details
    return participatingResponses.map(response => {
      const member = this.vo.members.find(m => {
        const memberId = m.userId || m.user?.id || m.id;
        return String(memberId) === String(response.userId);
      });

      // Handle snake_case from API
      const userFirstName = response.user?.firstName || response.user?.first_name || '';
      const userLastName = response.user?.lastName || response.user?.last_name || '';

      return {
        userId: response.userId,
        status: response.status,
        firstName: member?.firstName || member?.user?.firstName || userFirstName,
        lastName: member?.lastName || member?.user?.lastName || userLastName,
        username: member?.username || response.user?.username || response.username,
        // Include full member data if available
        ...(member || {})
      };
    });
  }

  /**
   * Get next upcoming event from events list
   */
  getNextUpcomingEvent() {
    const events = this.vo.events;
    if (!events || events.length === 0) return null;

    const now = new Date();

    const relevantEvents = events
      .filter(event => {
        if (event.status === 'completed') return false;
        if (!event.end_time) return true;  // No end time — treat as upcoming
        return new Date(event.end_time) > new Date(now.getTime() - 24 * 60 * 60 * 1000);
      })
      .sort((a, b) => {
        const aEnded = a.end_time && new Date(a.end_time) <= now;
        const bEnded = b.end_time && new Date(b.end_time) <= now;
        // Ongoing/upcoming first, ended events last
        if (aEnded && !bEnded) return 1;
        if (!aEnded && bEnded) return -1;
        // Both ongoing/upcoming: soonest start_time first
        if (!aEnded && !bEnded) return new Date(a.start_time || 0) - new Date(b.start_time || 0);
        // Both ended: most recently ended first
        return new Date(b.end_time) - new Date(a.end_time);
      });

    return relevantEvents.length > 0 ? relevantEvents[0] : null;
  }

  /**
   * Get event by ID from events list
   */
  getEventById(eventId) {
    return this.vo.events.find(e => e.id === eventId) || null;
  }

  // ==================== Setters ====================

  /**
   * Set group data
   */
  setGroup(group) {
    this.vo.group = group;
  }

  /**
   * Set members
   */
  setMembers(members) {
    this.vo.members = members;
    this._notifyStateChange('members', members);
  }

  /**
   * Set events list
   */
  setEvents(events) {
    this.vo.events = events;
  }

  /**
   * Set current event
   */
  setCurrentEvent(event) {
    this.vo.currentEvent.eventId = event?.id || null;
    this.vo.currentEvent.eventData = event;
  }

  /**
   * Set current game for the current event
   */
  setCurrentGame(gameType, gameData) {
    if (['poker', 'cricket', 'pingpong', 'pickleball'].includes(gameType)) {
      this.vo.currentEvent.games[gameType] = gameData;
    }
  }

  /**
   * Cache a game by eventId
   */
  cacheGame(gameType, eventId, gameData) {
    if (['poker', 'cricket', 'pingpong', 'pickleball'].includes(gameType)) {
      this.vo.existingGames[gameType][eventId] = gameData;
    }
  }

  /**
   * Get cached game for an event
   */
  getCachedGame(gameType, eventId) {
    return this.vo.existingGames[gameType]?.[eventId] || null;
  }

  /**
   * Reset current event state
   */
  resetCurrentEvent() {
    this.vo.currentEvent = {
      eventId: null,
      eventData: null,
      games: {
        poker: null,
        cricket: null,
        pingpong: null
      }
    };
  }

  /**
   * Update event in events list (e.g., after response change)
   */
  updateEvent(updatedEvent) {
    const index = this.vo.events.findIndex(e => e.id === updatedEvent.id);
    if (index !== -1) {
      this.vo.events[index] = updatedEvent;
    }

    // Also update currentEvent if it's the same event
    if (this.vo.currentEvent.eventId === updatedEvent.id) {
      this.vo.currentEvent.eventData = updatedEvent;
    }
  }

  // ==================== Game Operations ====================

  /**
   * Fetch game for an event from API
   */
  async fetchGameForEvent(eventId, sportName) {
    if (!eventId || !sportName) return null;

    const sportNameLower = sportName.toLowerCase();
    let gameData = null;

    try {
      if (sportNameLower === 'poker') {
        const games = await pokerGameAPI.getByEventId(eventId);
        if (Array.isArray(games) && games.length > 0) {
          gameData = games[0];
          this.cacheGame('poker', eventId, gameData);
        }
      } else if (sportNameLower === 'cricket') {
        const games = await cricketGameAPI.getByEventId(eventId);
        if (Array.isArray(games) && games.length > 0) {
          gameData = games[0];
          this.cacheGame('cricket', eventId, gameData);
        }
      } else if (sportNameLower === 'pingpong' || sportNameLower === 'ping pong' || sportNameLower === 'table tennis') {
        const games = await pingpongGameAPI.getByEventId(eventId);
        if (Array.isArray(games) && games.length > 0) {
          gameData = games[0];
          this.cacheGame('pingpong', eventId, gameData);
        }
      }

      return gameData;
    } catch (error) {
      console.error('[SquadDetailsProvider] Error fetching game for event:', error);
      return null;
    }
  }

  /**
   * Initialize poker game for an event
   */
  async initializePokerGame(event) {
    if (!event?.id) return null;

    try {
      // Check if game already exists
      const existingGames = await pokerGameAPI.getByEventId(event.id);
      if (Array.isArray(existingGames) && existingGames.length > 0) {
        const game = existingGames[0];
        this.cacheGame('poker', event.id, game);
        this.setCurrentGame('poker', game);
        return game;
      }

      // Create new game
      const today = new Date();
      const month = String(today.getMonth() + 1).padStart(2, '0');
      const day = String(today.getDate()).padStart(2, '0');
      const year = today.getFullYear();
      const tableName = `Poker-${month}/${day}/${year}`;

      const newGame = await pokerGameAPI.create({
        eventId: event.id,
        tableName,
        gameType: 'texas_holdem',
        smallBlind: 0,
        bigBlind: 0,
        chipValue100: 100,
        minimumBuyins: 100,
        buyinChipCount: 100,
        payoutCurrency: 'dollars',
      });

      if (newGame) {
        this.cacheGame('poker', event.id, newGame);
        this.setCurrentGame('poker', newGame);
        return newGame;
      }

      return null;
    } catch (error) {
      console.error('[SquadDetailsProvider] Error initializing poker game:', error);
      return null;
    }
  }

  /**
   * Load all games for events (batch load)
   */
  async loadAllGamesForEvents(events) {
    const sportName = this.getSportName();
    if (!sportName || !events || events.length === 0) return;

    const eventIds = events.map(e => e.id);

    try {
      if (sportName === 'poker') {
        const allGames = await pokerGameAPI.getAll({ eventIds });
        if (Array.isArray(allGames)) {
          allGames.forEach(game => {
            if (game.eventId) {
              this.cacheGame('poker', game.eventId, game);
            }
          });
        }
      } else if (sportName === 'cricket') {
        const allGames = await cricketGameAPI.getAll({ eventIds });
        if (Array.isArray(allGames)) {
          allGames.forEach(game => {
            if (game.eventId) {
              this.cacheGame('cricket', game.eventId, game);
            }
          });
        }
      } else if (sportName === 'ping pong' || sportName === 'table tennis') {
        const allGames = await pingpongGameAPI.getAll({ eventIds });
        if (Array.isArray(allGames)) {
          allGames.forEach(game => {
            if (game.eventId) {
              this.cacheGame('pingpong', game.eventId, game);
            }
          });
        }
      }
    } catch (error) {
      console.error('[SquadDetailsProvider] Error loading games:', error);
    }
  }
}

export default SquadDetailsProvider;
